/**
 * Buy menu (B).
 *
 * Every catalogue entry exists as a node from init(); opening the menu only
 * toggles classes. Slots belonging to the other team are hidden, slots the
 * player cannot afford are marked `poor`, and the first ten visible slots get
 * number keys 1..9,0.
 */
import { WEAPONS, GEAR } from '../../net/protocol.js';
import { el, setNum, setClass, setStyle, setText } from './dom.js';

const SLOT_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const TOTAL = WEAPONS.length + GEAR.length;

export class BuyMenu {
  constructor(parent) {
    const root = el('div', 'cs-buy', parent);
    const panel = el('div', 'cs-buy-panel', root);
    const head = el('div', 'cs-buy-head', panel);
    el('span', 'cs-buy-title', head, 'BUY');
    const money = el('span', 'cs-buy-money', head);
    el('span', 'cs-cur', money, '$');
    this.money = el('span', 'cs-amt', money, '0');
    const grid = el('div', 'cs-buy-grid', panel);

    /** Set by CsHudSystem; sends the request to the net subsystem. */
    this.onBuy = null;

    this.slots = [];
    let i = 0;
    for (const w of WEAPONS) {
      if (w.kind === 'melee') continue; // the knife is not purchasable
      this.slots.push(this._slot(grid, i++, w.key, w.name, w.cost ?? 0, w.team | 0));
    }
    for (const g of GEAR) {
      this.slots.push(this._slot(grid, i++, g.key, g.name, g.cost ?? 0, g.team | 0));
    }
    el('div', 'cs-buy-foot', panel, '1–0 buy · b or esc close');

    /** Visible, in-menu-order slots, rebuilt (by assignment) on refresh. */
    this.order = new Array(TOTAL).fill(null);
    this.root = root;
    this.grid = grid;
    this.open = false;

    grid.addEventListener('click', (e) => {
      const node = e.target?.closest?.('.cs-buy-item');
      if (!node || !node.__key) return;
      if (node.classList.contains('poor')) return;
      this.onBuy?.(node.__key);
    });
  }

  _slot(parent, index, key, name, cost, team) {
    const node = el('button', 'cs-buy-item', parent);
    node.type = 'button';
    node.__key = key;
    const slot = el('span', 'cs-buy-slot', node, SLOT_KEYS[index] ?? '·');
    const label = el('span', 'cs-buy-name', node, name ?? key);
    const price = el('span', 'cs-buy-cost', node, '$' + cost);
    return { node, slot, label, price, key, cost, team, visible: false, poor: false };
  }

  setOpen(on) {
    if (this.open === on) return;
    this.open = on;
    setClass(this.root, 'on', on);
    if (on && document.pointerLockElement) document.exitPointerLock();
  }

  /** Number key 0-9 pressed while open; index into the visible ordering. */
  key(index) {
    const slot = this.order[index | 0];
    if (!slot || slot.poor) return false;
    this.onBuy?.(slot.key);
    return true;
  }

  update(s) {
    if (!this.open) return;
    const money = s?.money ?? 0;
    const team = s?.team ?? 0;
    setNum(this.money, money);

    let n = 0;
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      const visible = slot.team === 0 || slot.team === team;
      setClass(slot.node, 'on', visible);
      slot.visible = visible;
      if (!visible) continue;
      const poor = slot.cost > money;
      setClass(slot.node, 'poor', poor);
      slot.poor = poor;
      setText(slot.slot, SLOT_KEYS[n] ?? '·');
      if (n < SLOT_KEYS.length) this.order[n] = slot;
      n++;
    }
    for (let i = Math.max(n, 0); i < TOTAL; i++) this.order[i] = null;
  }

  dispose() {
    setStyle(this.root, 'display', 'none');
  }
}
